import { useMemo } from 'react';
import { useContactUsDictionary } from '@/shared/api';
import { createMailto, getDeviceInfo, formatEmailBody } from '@/shared/utils';
import { APP_NAME, DATE_TIME_FORMAT } from '@/shared/config';
import { CONTACT_US_EMAIL_TYPES, type ContactUsEmailType } from '../types';
import { useVisitHistoryInfinity } from '@/widgets/user-profile/visit-history';
import { formatDate } from 'date-fns';
import { useSessionStore } from '@/entities/session';

type Props = {
  ewalletAccNum?: string;
};

const useContactUs = ({ ewalletAccNum }: Props = {}) => {
  const { session } = useSessionStore();
  const { contactUsDictionaryData, isContactUsDictionaryLoading } =
    useContactUsDictionary();

  const { items: visitHistoryItems, isLoading: isVisitHistoryLoading } =
    useVisitHistoryInfinity({
      enabled: !!session?.userId,
    });

  const links = useMemo(() => {
    const dict = contactUsDictionaryData?.value;
    if (!dict) return null;

    let lastLogin = visitHistoryItems?.[0]?.loginDateTime || '';

    if (lastLogin) {
      lastLogin = formatDate(lastLogin, DATE_TIME_FORMAT);
    }

    const formatBody = (raw: string) => {
      if (!raw) return '';
      const { os } = getDeviceInfo();
      return formatEmailBody(
        raw
          .replace(/<ewallet>/g, ewalletAccNum ?? '')
          .replace(/<app>/g, APP_NAME)
          .replace(/<os>/g, os)
          .replace(/<last_login>/g, lastLogin)
      );
    };

    const buildLink = (tmpl: {
      email: string;
      subject: string;
      body: string;
    }) =>
      createMailto({
        to: tmpl.email,
        subject: tmpl.subject,
        body: formatBody(tmpl.body),
        extras: { ewallet: ewalletAccNum },
      });

    const result: Record<ContactUsEmailType, string | null> = {
      changeAccountInfoRequest: null,
      deleteAccountRequest: null,
      turnoffTwofaRequest: null,
      verificationForCorporate: null,
      defaultRequest: null,
    };

    Object.values(CONTACT_US_EMAIL_TYPES).forEach(type => {
      const template =
        type === CONTACT_US_EMAIL_TYPES.DEFAULT_REQUEST ? dict : dict[type];

      result[type] = template ? buildLink(template) : null;
    });

    return result;
  }, [contactUsDictionaryData, ewalletAccNum, visitHistoryItems]);

  return {
    links,
    isLoading: isContactUsDictionaryLoading || isVisitHistoryLoading,
  };
};

export { useContactUs };
